import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getStoredConsent,
  storeConsent,
  initConsentedTrackers,
} from "@/lib/consent";

// One-time, dismissible cookie/session disclosure. Shown only when no consent
// choice is stored. Accepting loads the gated trackers immediately (no reload);
// declining keeps them off. The choice persists in localStorage so returning
// visitors never see this again.
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => getStoredConsent() === null);

  // While the fixed banner is shown, reserve space at the bottom of the page so
  // it never covers footer content (e.g. the "Do Not Sell or Share" link).
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "9rem";
    return () => {
      document.body.style.paddingBottom = prev;
    };
  }, [visible]);

  if (!visible) return null;

  const accept = () => {
    storeConsent("accepted");
    initConsentedTrackers();
    // AnalyticsTracker only emits page_view on route changes, so the page the
    // visitor accepted on would otherwise go uncounted until they navigate.
    // gtag is set synchronously by loadGoogleAnalytics and queues into dataLayer
    // until the script finishes loading, so this is safe to call immediately.
    const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag === "function") {
      gtag("event", "page_view", {
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    setVisible(false);
  };

  const decline = () => {
    storeConsent("declined");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie and session disclosure"
      className="fixed bottom-0 left-0 right-0 z-[9998] border-t px-4 py-4 sm:px-6 backdrop-blur"
      style={{
        backgroundColor: "var(--gbz-card)",
        borderColor: "var(--gbz-hairline)",
      }}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Cookie
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: "var(--gbz-gold)" }}
          />
          <p
            className="text-sm leading-relaxed"
            style={{ color: "var(--gbz-fg)" }}
          >
            GOBAZAAR uses essential session cookies and local storage to keep you
            signed in and remember your preferences. With your consent, we also
            use Google Analytics and Sentry session-replay tooling to understand
            usage and diagnose errors. See our{" "}
            <Link
              href="/privacy"
              className="underline underline-offset-2"
              style={{ color: "var(--gbz-accent)" }}
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 gap-2 sm:justify-end">
          <Button variant="outline" size="sm" onClick={decline}>
            Decline Cookies
          </Button>
          <Button size="sm" onClick={accept}>
            Accept Cookies
          </Button>
        </div>
      </div>
    </div>
  );
}
